import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useESGDataEntryStore,
  useESGDisclosureSelectionStore,
} from "@/features/esg/hooks/useESGReportStore";
import { useTran } from "@/hooks";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import DataEntryDisclosureSidebar from "./DataEntryDisclosureSidebar";
import DataEntry from "./DataEntry";
import DataEntryDescription from "./DataEntryDescription";
import { ResizableSidebar } from "@/components/common";

const DataEntryContent = observer(() => {
  const { t } = useTran();
  const {
    activeCategory,
    currentDisclosureCode,
    setCurrentDisclosure,
    completedCountByCategory,
    progressPercentage,
  } = useESGDataEntryStore();

  const { selectedCodesByCategory, filteredDisclosures } =
    useESGDisclosureSelectionStore();

  const matchingDisclosures = filteredDisclosures.filter(
    (d) => d.code === currentDisclosureCode,
  );
  const disclosure = matchingDisclosures[0] || {};
  const categoryCodes = selectedCodesByCategory[activeCategory] || [];
  const currentIndex = categoryCodes.indexOf(currentDisclosureCode);
  const completedInCategory = completedCountByCategory(activeCategory);
  const totalInCategory = categoryCodes.length;

  useEffect(() => {
    if (
      categoryCodes.length > 0 &&
      !categoryCodes.includes(currentDisclosureCode)
    ) {
      setCurrentDisclosure(categoryCodes[0]);
    }
  }, [categoryCodes, currentDisclosureCode, setCurrentDisclosure]);

  return (
    <div className="flex-1 min-h-0 flex flex-col rounded-radius bg-background/30 p-4 border-border border gap-y-3">
      <div className="grid grid-cols-10 gap-3 items-center">
        <p className="font-semibold col-span-3">{t(activeCategory)}</p>
        <div className="flex items-center justify-end gap-2 col-span-5">
          <Button
            variant="outline"
            size="sm"
            disabled={currentIndex <= 0}
            onClick={() =>
              setCurrentDisclosure(categoryCodes[currentIndex - 1])
            }
          >
            {t("Previous")}
          </Button>
          <Badge variant="outline" className="bg-muted">
            {t(`item ${currentIndex + 1} of ${totalInCategory}`)}
          </Badge>
          <Button
            size="sm"
            disabled={currentIndex >= totalInCategory - 1}
            onClick={() =>
              setCurrentDisclosure(categoryCodes[currentIndex + 1])
            }
          >
            {t("Next")}
          </Button>
        </div>
        <div className="flex items-center justify-end gap-2 col-span-2">
          <Badge className="bg-blue-500/10 border-blue-500/50 border rounded-md text-blue-500">
            {`${completedInCategory}/${totalInCategory} completed`}
          </Badge>
          <Badge>{progressPercentage}%</Badge>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex gap-3 overflow-hidden">
        <ResizableSidebar
          storageKey="nxstart_esg_data_entry_sidebar_width"
          defaultWidth={300}
          minWidth={240}
          maxWidth={360}
          className="min-h-0 h-full"
          handleTitle={t("Drag to resize")}
        >
          <DataEntryDisclosureSidebar />
        </ResizableSidebar>

        <div className="flex-1 min-w-0 min-h-0 h-full">
          <DataEntry
            matchingDisclosures={matchingDisclosures}
            disclosure={disclosure}
          />
        </div>

        <ResizableSidebar
          storageKey="nxstart_esg_data_entry_description_width"
          defaultWidth={280}
          minWidth={240}
          maxWidth={360}
          className="min-h-0 h-full"
          handlePosition="left"
          handleTitle={t("Drag to resize")}
        >
          <DataEntryDescription disclosure={disclosure} />
        </ResizableSidebar>
      </div>
    </div>
  );
});

export default DataEntryContent;
